import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../api/client';
import { Link } from 'react-router-dom';
import { MetricCard, PageHeader, PageShell, StatusBadge } from '../components/ui';
import { InterestTranslationFields } from '../components/InterestTranslationFields';

type InterestTypeDto = {
  id: string;
  name: string;
  translationKey?: string;
  nameEn?: string;
  nameDe?: string;
  nameCs?: string;
};
type InterestDto = {
  id: string;
  name: string;
  type: InterestTypeDto;
  translationKey?: string;
  nameEn?: string;
  nameDe?: string;
  nameCs?: string;
};
type DefaultInterestsSetupResult = {
  createdTypes: number;
  updatedTypes: number;
  existingTypes: number;
  createdInterests: number;
  updatedInterests: number;
  existingInterests: number;
};

export const InterestsOverviewPage: React.FC = () => {
  const { request } = useApiClient();
  const [types, setTypes] = useState<InterestTypeDto[]>([]);
  const [interests, setInterests] = useState<InterestDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [setupMessage, setSetupMessage] = useState<string | null>(null);
  const [interestActionId, setInterestActionId] = useState<string | null>(null);
  const [typeActionId, setTypeActionId] = useState<string | null>(null);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [typeEditForm, setTypeEditForm] = useState({ name: '', translationKey: '', nameEn: '', nameDe: '', nameCs: '' });
  const [editingInterestId, setEditingInterestId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', translationKey: '', nameEn: '', nameDe: '', nameCs: '' });

  const loadInterests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, i] = await Promise.all([
        request<InterestTypeDto[]>('/interests/types'),
        request<InterestDto[]>('/interests'),
      ]);
      setTypes(t);
      setInterests(i);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadInterests();
  }, [loadInterests]);

  const typeInterestCounts = useMemo(() => {
    return interests.reduce<Record<string, number>>((acc, interest) => {
      const typeId = interest.type?.id;
      if (typeId) acc[typeId] = (acc[typeId] || 0) + 1;
      return acc;
    }, {});
  }, [interests]);

  const handleDeleteInterest = async (id: string) => {
    setInterestActionId(id);
    setError(null);
    try {
      await request(`/interests/${encodeURIComponent(id)}`, { method: 'DELETE' });
      setInterests((prev) => prev.filter((interest) => interest.id !== id));
    } catch (e) {
      setError(String(e));
    } finally {
      setInterestActionId(null);
    }
  };

  const handleDeleteType = async (id: string) => {
    setTypeActionId(id);
    setError(null);
    try {
      await request(`/interests/types/${encodeURIComponent(id)}`, { method: 'DELETE' });
      setTypes((prev) => prev.filter((type) => type.id !== id));
    } catch (e) {
      setError(String(e));
    } finally {
      setTypeActionId(null);
    }
  };

  const handleSetupDefaults = async () => {
    setSetupLoading(true);
    setSetupMessage(null);
    setError(null);
    try {
      const result = await request<DefaultInterestsSetupResult>('/interests/defaults', { method: 'POST' });
      await loadInterests();
      const touchedInterests = result.createdInterests + result.updatedInterests;
      const touchedTypes = result.createdTypes + result.updatedTypes;
      setSetupMessage(`Set up ${touchedInterests} interests and ${touchedTypes} types. ${result.existingInterests} interests were already present.`);
      setSetupComplete(true);
    } catch (e) {
      setError(String(e));
    } finally {
      setSetupLoading(false);
    }
  };

  const startEditingType = (type: InterestTypeDto) => {
    setEditingTypeId(type.id);
    setTypeEditForm({
      name: type.name || '',
      translationKey: type.translationKey || '',
      nameEn: type.nameEn || '',
      nameDe: type.nameDe || '',
      nameCs: type.nameCs || '',
    });
  };

  const cancelEditType = () => {
    setEditingTypeId(null);
    setTypeEditForm({ name: '', translationKey: '', nameEn: '', nameDe: '', nameCs: '' });
  };

  const submitEditType = async () => {
    if (!editingTypeId) return;
    setTypeActionId(editingTypeId);
    setError(null);
    try {
      const updated = await request<InterestTypeDto>(`/interests/types/${encodeURIComponent(editingTypeId)}`, {
        method: 'PATCH',
        body: JSON.stringify(typeEditForm),
      });
      setTypes((prev) => prev.map((type) => (type.id === updated.id ? { ...type, ...updated } : type)));
      setInterests((prev) => prev.map((interest) => interest.type?.id === updated.id
        ? { ...interest, type: { ...interest.type, ...updated } } : interest));
      cancelEditType();
    } catch (e) {
      setError(String(e));
    } finally {
      setTypeActionId(null);
    }
  };

  const hasTypeTranslations = (t: InterestTypeDto) => Boolean(t.translationKey || t.nameEn || t.nameDe || t.nameCs);

  const startEditingInterest = (interest: InterestDto) => {
    setEditingInterestId(interest.id);
    setEditForm({
      name: interest.name || '',
      translationKey: interest.translationKey || '',
      nameEn: interest.nameEn || '',
      nameDe: interest.nameDe || '',
      nameCs: interest.nameCs || '',
    });
  };

  const cancelEditInterest = () => {
    setEditingInterestId(null);
    setEditForm({ name: '', translationKey: '', nameEn: '', nameDe: '', nameCs: '' });
  };

  const submitEditInterest = async () => {
    if (!editingInterestId) return;
    setInterestActionId(editingInterestId);
    setError(null);
    try {
      const updated = await request<InterestDto>(`/interests/${encodeURIComponent(editingInterestId)}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm),
      });
      setInterests((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
      cancelEditInterest();
    } catch (e) {
      setError(String(e));
    } finally {
      setInterestActionId(null);
    }
  };

  const hasTranslations = (it: InterestDto) => Boolean(it.translationKey || it.nameEn || it.nameDe || it.nameCs);
  const translatedInterests = interests.filter(hasTranslations).length;

  return (
    <PageShell>
      <PageHeader
        title="Interests"
        description="Manage interest types and the options users can select in their profiles."
        actions={(
          <>
            <button className="btn btn-neutral" onClick={handleSetupDefaults} disabled={setupLoading || setupComplete} title="Create the default carpool conversation interests">
              {setupLoading ? 'Setting up…' : setupComplete ? 'Interests set up' : 'Set up carpool interests'}
            </button>
            <Link className="btn btn-neutral" to="/interests/new-type">New type</Link>
            <Link className="btn btn-primary" to="/interests/new">New interest</Link>
          </>
        )}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard label="Interest types" value={loading ? '—' : types.length} tone="violet" />
        <MetricCard label="Interests" value={loading ? '—' : interests.length} tone="blue" />
        <MetricCard label="Translated interests" value={loading ? '—' : `${translatedInterests} / ${interests.length}`} tone="green" />
      </div>

      {error && <div className="admin-interest-alert mt-4" role="alert">{error}</div>}
      {setupMessage && <div className="admin-interest-success mt-4" role="status">{setupMessage}</div>}
      {loading ? <div className="admin-panel mt-4 p-5 text-sm text-slate-500" role="status">Loading interests…</div> : !error && types.length === 0 && interests.length === 0 ? (
        <div className="admin-panel mt-4 p-8 text-center"><p className="text-sm text-slate-600">No interest types or interests yet.</p><p className="mt-1 text-xs text-slate-500">No interests</p><Link className="btn btn-primary mt-3" to="/interests/new-type">Create an interest type</Link></div>
      ) : (
        <div className="mt-4 grid items-start gap-4 xl:grid-cols-2">
          <section className="admin-panel p-5" aria-labelledby="interest-types-heading">
            <div className="admin-section-heading"><div><h2 id="interest-types-heading">Types</h2><p>Types organize related profile interests.</p></div><span className="admin-count-pill">{types.length}</span></div>
            <div className="grid gap-2">
              {types.map((interestType) => {
                const isEditing = editingTypeId === interestType.id;
                const usage = typeInterestCounts[interestType.id] || 0;
                const deleting = typeActionId === interestType.id && !isEditing;
                const isSaving = typeActionId === interestType.id && isEditing;
                return (
                  <article key={interestType.id} className="admin-interest-entry">
                    {isEditing ? (
                      <div className="grid gap-3">
                        <label className="admin-form-label">Name<input className="form-control mt-1 w-full" placeholder="Name" value={typeEditForm.name} onChange={(event) => setTypeEditForm({ ...typeEditForm, name: event.target.value })} /></label>
                        <InterestTranslationFields value={typeEditForm} onChange={(field, value) => setTypeEditForm((previous) => ({ ...previous, [field]: value }))} />
                        <div className="flex flex-wrap gap-2 border-t pt-3">
                          <button type="button" className="btn btn-primary" onClick={submitEditType} disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</button>
                          <button type="button" className="btn btn-neutral" onClick={cancelEditType} disabled={isSaving}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex min-w-0 items-start justify-between gap-3">
                          <div className="min-w-0"><h3 className="admin-interest-name">{interestType.name}</h3><div className="admin-interest-meta">{usage} {usage === 1 ? 'interest' : 'interests'}</div></div>
                          <div className="flex shrink-0 gap-2">
                            <button type="button" className="btn btn-neutral" onClick={() => startEditingType(interestType)} title="Edit type">Edit</button>
                            <button type="button" className="btn btn-danger" onClick={() => handleDeleteType(interestType.id)} disabled={usage > 0 || deleting} title={usage > 0 ? 'Remove all interests before deleting' : 'Delete type'} aria-label={`Delete type ${interestType.name}`}>{deleting ? '…' : 'Delete'}</button>
                          </div>
                        </div>
                        {hasTypeTranslations(interestType) && <StatusBadge tone="green" className="mt-3">Translations available</StatusBadge>}
                      </>
                    )}
                  </article>
                );
              })}
              {!types.length && <p className="admin-interest-empty">No types</p>}
            </div>
          </section>

          <section className="admin-panel p-5" aria-labelledby="interests-heading">
            <div className="admin-section-heading"><div><h2 id="interests-heading">Interests</h2><p>These options appear in users’ profiles.</p></div><span className="admin-count-pill">{interests.length}</span></div>
            <div className="grid gap-2">
              {interests.map((interest) => {
                const isEditing = editingInterestId === interest.id;
                const isSaving = interestActionId === interest.id && isEditing;
                return (
                  <article key={interest.id} className="admin-interest-entry">
                    {isEditing ? (
                      <div className="grid gap-3">
                        <label className="admin-form-label">Name<input className="form-control mt-1 w-full" placeholder="Name" value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} /></label>
                        <InterestTranslationFields value={editForm} onChange={(field, value) => setEditForm((previous) => ({ ...previous, [field]: value }))} />
                        <div className="flex flex-wrap gap-2 border-t pt-3">
                          <button type="button" className="btn btn-primary" onClick={submitEditInterest} disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</button>
                          <button type="button" className="btn btn-neutral" onClick={cancelEditInterest} disabled={isSaving}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex min-w-0 items-start justify-between gap-3">
                        <div className="min-w-0"><h3 className="admin-interest-name">{interest.name}</h3><div className="admin-interest-meta">{interest.type?.name || 'Uncategorized'}</div>{hasTranslations(interest) && <StatusBadge tone="green" className="mt-3">Translations available</StatusBadge>}</div>
                        <div className="flex shrink-0 gap-2">
                          <button type="button" className="btn btn-neutral" onClick={() => startEditingInterest(interest)} title="Edit translations">Edit</button>
                          <button type="button" className="btn btn-danger" onClick={() => handleDeleteInterest(interest.id)} aria-label="Delete interest" disabled={interestActionId === interest.id} title="Delete interest">{interestActionId === interest.id ? '…' : 'Delete'}</button>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
              {!interests.length && <p className="admin-interest-empty">No interests</p>}
            </div>
          </section>
        </div>
      )}
    </PageShell>
  );
};
